import { WebViewerInstance, Core } from '@pdftron/webviewer';
import { ManualAlignZoneCustomData } from 'types';

/** Smallest width or height an anchored zone keeps while resizing, in page points. */
export const MINIMUM_ANCHORED_ZONE_SIZE = 1;

export type AnchoredResizeHandle = 'corner' | 'width-edge' | 'height-edge';

export interface AnchoredZoneRect {
  X: number;
  Y: number;
  Width: number;
  Height: number;
}

export interface AnchoredResizeState {
  initialAnchorPagePosition: { x: number; y: number };
  initialAnnotationRect: AnchoredZoneRect;
  initialAspectRatio: number | null;
}

const activeResizeHandles = new WeakMap<object, AnchoredResizeHandle>();

export function setActiveResizeHandle(annotation: object, handle: AnchoredResizeHandle): void {
  activeResizeHandles.set(annotation, handle);
}

export function clearActiveResizeHandle(annotation: object): void {
  activeResizeHandles.delete(annotation);
}

/**
 * After PDFTron's default resize, pin the anchor and either keep the drag-start
 * aspect ratio (corners, and any drag while the zone is locked) or change one
 * axis (edge handles while unlocked).
 */
export function resizeAnchoredZone(
  selected: AnchoredZoneRect,
  anchorPoint: { x: number; y: number },
  state: AnchoredResizeState,
  handle: AnchoredResizeHandle,
  isAspectRatioUnlocked: boolean,
): void {
  const stretchesOneAxis = isAspectRatioUnlocked && (handle === 'width-edge' || handle === 'height-edge');

  if (handle === 'width-edge' && stretchesOneAxis) {
    selected.Height = state.initialAnnotationRect.Height;
    selected.Width = Math.max(MINIMUM_ANCHORED_ZONE_SIZE, selected.Width);
  } else if (handle === 'height-edge' && stretchesOneAxis) {
    selected.Width = state.initialAnnotationRect.Width;
    selected.Height = Math.max(MINIMUM_ANCHORED_ZONE_SIZE, selected.Height);
  } else if (!state.initialAspectRatio || !isFinite(state.initialAspectRatio)) {
    return;
  } else {
    const widthChange = Math.abs(selected.Width - state.initialAnnotationRect.Width);
    const heightChange = Math.abs(selected.Height - state.initialAnnotationRect.Height);

    if (widthChange > heightChange) {
      selected.Height = selected.Width / state.initialAspectRatio;
    } else {
      selected.Width = selected.Height * state.initialAspectRatio;
    }

    if (selected.Width < MINIMUM_ANCHORED_ZONE_SIZE) {
      selected.Width = MINIMUM_ANCHORED_ZONE_SIZE;
      selected.Height = selected.Width / state.initialAspectRatio;
    }
    if (selected.Height < MINIMUM_ANCHORED_ZONE_SIZE) {
      selected.Height = MINIMUM_ANCHORED_ZONE_SIZE;
      selected.Width = selected.Height * state.initialAspectRatio;
    }
  }

  const anchorXAfter = selected.X + anchorPoint.x * selected.Width;
  const anchorYAfter = selected.Y + anchorPoint.y * selected.Height;
  selected.X += state.initialAnchorPagePosition.x - anchorXAfter;
  selected.Y += state.initialAnchorPagePosition.y - anchorYAfter;
}

/**
 * Register the AnchorResizeTool to override the default AnnotationEdit tool
 * Custom AnnotationEditTool that supports anchor-locked resizing.
 * When an annotation has an anchor point set (stored in customData as 'anchorPoint'),
 * resizing will keep that anchor point fixed in place.
 */
export function registerAnchorResizeTool(instance: WebViewerInstance): void {
  const { documentViewer } = instance.Core;
  const { Tools } = instance.Core;

  /**
   * Parse and validate anchor point data from customData
   * Returns null if parsing fails or if x/y properties are missing/invalid
   */
  function parseAndValidateAnchorPoint(anchorPointData: string): { x: number; y: number } | null {
    try {
      const parsed = JSON.parse(anchorPointData);
      // Validate that x and y exist and are valid numbers
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof parsed.x === 'number' &&
        typeof parsed.y === 'number' &&
        isFinite(parsed.x) &&
        isFinite(parsed.y)
      ) {
        // Clamp to [0, 1] to guard against invalid values that would
        // place the anchor outside the annotation rect and cause large jumps.
        return {
          x: Math.max(0, Math.min(1, parsed.x)),
          y: Math.max(0, Math.min(1, parsed.y)),
        };
      }
    } catch (error) {
      // Invalid JSON or missing properties
    }
    return null;
  }

  function applyAnchorLockedResize(
    selected: Core.Annotations.Annotation,
    anchorPoint: { x: number; y: number },
    state: AnchoredResizeState,
    annotationManager: Core.AnnotationManager,
  ): void {
    const handle = activeResizeHandles.get(selected) ?? 'corner';
    const isAspectRatioUnlocked = selected.getCustomData(ManualAlignZoneCustomData.aspectRatioUnlocked) === 'true';
    resizeAnchoredZone(selected, anchorPoint, state, handle, isAspectRatioUnlocked);
    annotationManager.redrawAnnotation(selected);
  }

  /**
   * Shared state manager for anchor-locked resize tracking.
   * Used by both AnchorResizeTool (AnnotationEditTool override) and
   * the AnnotationSelectTool prototype patches to avoid duplicated logic.
   */
  class AnchorResizeTracker {
    private state = new Map<string, AnchoredResizeState>();
    private currentDragAnnotationId: string | null = null;

    /**
     * Call from mouseLeftDown: captures initial annotation geometry and anchor
     * position so the subsequent mouseMove can keep the anchor fixed.
     */
    onMouseLeftDown(annotationManager: Core.AnnotationManager): void {
      const selectedAnnotations = annotationManager.getSelectedAnnotations();
      const selected = selectedAnnotations.length > 0 ? selectedAnnotations[0] : null;

      // Default to null - only set if we successfully set up anchor-locked resize
      this.currentDragAnnotationId = null;

      // Store initial annotation state and anchor position in page coordinates if it has an anchor point
      if (selected) {
        clearActiveResizeHandle(selected);
        const anchorPointData = selected.getCustomData('anchorPoint');
        if (anchorPointData && anchorPointData !== 'none') {
          // Parse and validate anchor point (normalized coordinates 0-1)
          const anchorPoint = parseAndValidateAnchorPoint(anchorPointData);
          if (anchorPoint) {
            // Store initial anchor position in page coordinates
            const initialAnchorPagePosition = {
              x: selected.X + anchorPoint.x * selected.Width,
              y: selected.Y + anchorPoint.y * selected.Height,
            };
            // Store initial aspect ratio for maintaining proportions
            // Guard against division by zero - if height is 0, we can't maintain aspect ratio
            const initialAspectRatio = selected.Height !== 0 ? selected.Width / selected.Height : null;
            this.state.set(selected.Id, {
              initialAnchorPagePosition,
              initialAnnotationRect: {
                X: selected.X,
                Y: selected.Y,
                Width: selected.Width,
                Height: selected.Height,
              },
              initialAspectRatio,
            });
            // Track which annotation is being dragged
            this.currentDragAnnotationId = selected.Id;
          }
        }
      }
    }

    /**
     * Call from mouseLeftUp: cleans up resize state for the annotation that
     * was being dragged.
     */
    onMouseLeftUp(): void {
      // Clean up state for the annotation that was originally being dragged,
      // not the currently selected one (selection may have changed)
      if (this.currentDragAnnotationId) {
        this.state.delete(this.currentDragAnnotationId);
        this.currentDragAnnotationId = null;
      }
    }

    /**
     * Call from mouseMove: if the selected annotation has an anchor point and
     * we have tracked resize state, runs the default resize via `callSuper`,
     * then corrects position/aspect-ratio to keep the anchor fixed.
     *
     * Returns a wrapper `{ handled: true, value }` when anchor-locked resize
     * is applied, or `null` to indicate the caller should fall through to
     * default behavior. The wrapper avoids a sentinel collision where
     * `callSuper()` returning `null` would be mistaken for "not handled."
     */
    onMouseMove(
      annotationManager: Core.AnnotationManager,
      e: any,
      callSuper: () => any,
    ): { handled: true; value: any } | null {
      const selectedAnnotations = annotationManager.getSelectedAnnotations();
      const selected = selectedAnnotations.length > 0 ? selectedAnnotations[0] : null;

      if (!selected || !e.buttons) {
        return null;
      }

      const anchorPointData = selected.getCustomData('anchorPoint');
      if (!anchorPointData || anchorPointData === 'none') {
        return null;
      }

      const resizeState = this.state.get(selected.Id);
      // Check that aspect ratio is valid (not null, Infinity, or NaN)
      if (!resizeState || resizeState.initialAspectRatio === null || !isFinite(resizeState.initialAspectRatio)) {
        return null;
      }

      // Parse and validate anchor point (stored as normalized coordinates 0-1)
      const anchorPoint = parseAndValidateAnchorPoint(anchorPointData);
      if (!anchorPoint) {
        return null;
      }

      // Let default resize run first - this allows the annotation to resize normally
      const result = callSuper();
      applyAnchorLockedResize(selected, anchorPoint, resizeState, annotationManager);
      return { handled: true, value: result };
    }
  }

  /**
   * Custom AnnotationEditTool that supports anchor-locked resizing
   */
  class AnchorResizeTool extends Tools.AnnotationEditTool {
    private tracker = new AnchorResizeTracker();

    constructor(docViewer: Core.DocumentViewer, name?: string) {
      super(docViewer, name);
    }

    mouseLeftDown(e: any) {
      this.tracker.onMouseLeftDown(this.docViewer.getAnnotationManager());
      return super.mouseLeftDown(e);
    }

    mouseLeftUp(e: any) {
      this.tracker.onMouseLeftUp();
      return super.mouseLeftUp(e);
    }

    mouseMove(e: any) {
      const result = this.tracker.onMouseMove(this.docViewer.getAnnotationManager(), e, () => super.mouseMove(e));
      return result !== null ? result.value : super.mouseMove(e);
    }
  }

  // Create and register the custom tool
  const anchorResizeTool = new AnchorResizeTool(documentViewer);

  // Override the default AnnotationEdit tool
  const toolModeMap = documentViewer.getToolModeMap();
  if (toolModeMap) {
    toolModeMap.AnnotationEdit = anchorResizeTool;

    // CRITICAL: Also override AnnotationSelectTool's mouseMove since resize operations
    // often go through AnnotationSelectTool, not AnnotationEditTool
    const selectToolTracker = new AnchorResizeTracker();

    const originalSelectToolMouseMove = Tools.AnnotationSelectTool.prototype.mouseMove;
    const originalSelectToolMouseLeftDown = Tools.AnnotationSelectTool.prototype.mouseLeftDown;
    const originalSelectToolMouseLeftUp = Tools.AnnotationSelectTool.prototype.mouseLeftUp;

    Tools.AnnotationSelectTool.prototype.mouseMove = function (e: any) {
      const annotationManager = documentViewer.getAnnotationManager();
      const result = selectToolTracker.onMouseMove(annotationManager, e, () =>
        originalSelectToolMouseMove.call(this, e),
      );
      return result !== null ? result.value : originalSelectToolMouseMove.call(this, e);
    };

    Tools.AnnotationSelectTool.prototype.mouseLeftDown = function (e: any) {
      selectToolTracker.onMouseLeftDown(documentViewer.getAnnotationManager());
      return originalSelectToolMouseLeftDown.call(this, e);
    };

    Tools.AnnotationSelectTool.prototype.mouseLeftUp = function (e: any) {
      selectToolTracker.onMouseLeftUp();
      try {
        return originalSelectToolMouseLeftUp.call(this, e);
      } catch {
        // PDFTron crashes inside mouseLeftUp when its internal state is in an unexpected
        // condition (e.g. during annotation select/deselect sequences while the content
        // overlay is visible). Root cause is inside minified PDFTron code so exact trigger
        // is unclear. Swallowing prevents the tool from entering an unrecoverable broken state.
      }
    };
  }
}
